import { createContext, useContext, useState } from 'react';

import { useSearchParams } from 'react-router-dom';

import { AnimalRow, ProcedureRow } from '@/services/animal-management';

export type ActiveAnimalGrid = 'animals' | 'procedures';

export type AnimalManagementContextType = {
  activeGrid: ActiveAnimalGrid;
  selectedAnimals: AnimalRow[];
  selectedProcedures: ProcedureRow[];
  setActiveGrid: (grid: ActiveAnimalGrid) => void;
  setSelectedAnimals: (rows: AnimalRow[]) => void;
  setSelectedProcedures: (rows: ProcedureRow[]) => void;
};

const AnimalManagementContext = createContext<
  AnimalManagementContextType | undefined
>(undefined);

export function AnimalManagementProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeGrid: ActiveAnimalGrid =
    searchParams.get('tab') === 'procedures' ? 'procedures' : 'animals';
  const setActiveGrid = (grid: ActiveAnimalGrid) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set('tab', grid);
        return next;
      },
      { replace: true },
    );
  };
  const [selectedAnimals, setSelectedAnimals] = useState<AnimalRow[]>([]);
  const [selectedProcedures, setSelectedProcedures] = useState<ProcedureRow[]>(
    [],
  );

  return (
    <AnimalManagementContext.Provider
      value={{
        activeGrid,
        selectedAnimals,
        selectedProcedures,
        setActiveGrid,
        setSelectedAnimals,
        setSelectedProcedures,
      }}
    >
      {children}
    </AnimalManagementContext.Provider>
  );
}

export function useAnimalManagementContext() {
  const context = useContext(AnimalManagementContext);

  if (!context) {
    throw new Error(
      'useAnimalManagementContext must be used within AnimalManagementProvider',
    );
  }

  return context;
}
